import { isValidWorkspaceIdFormat } from '@/lib/auth/workspaceAuthority';

export type RoutePlane = 'platform' | 'customer';

export type CustomerRouteTarget =
  | 'dashboard'
  | 'companies'
  | 'projects'
  | 'project'
  | 'milestones'
  | 'milestoneGate'
  | 'calendar'
  | 'tasks'
  | 'task'
  | 'assets'
  | 'documents'
  | 'investments'
  | 'decisions'
  | 'reports'
  | 'chat'
  | 'users'
  | 'invitations'
  | 'invitationSettings'
  | 'security'
  | 'settings'
  | 'billing';

export type RouteBuildFailureReason =
  | 'missing-workspace'
  | 'invalid-workspace'
  | 'missing-resource-id'
  | 'cross-workspace-resource'
  | 'invalid-platform-route';

export type RouteBuildResult =
  | { readonly ok: true; readonly plane: RoutePlane; readonly href: string }
  | { readonly ok: false; readonly plane: RoutePlane; readonly reason: RouteBuildFailureReason };

export interface RouteQueryInput {
  readonly [key: string]: string | number | boolean | null | undefined;
}

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

function toSearchString(query?: RouteQueryInput | URLSearchParams | string): string {
  if (!query) return '';
  if (typeof query === 'string') {
    if (!query.trim()) return '';
    return query.startsWith('?') ? query : `?${query}`;
  }

  const params = query instanceof URLSearchParams ? query : new URLSearchParams();
  if (!(query instanceof URLSearchParams)) {
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined) continue;
      params.set(key, String(value));
    }
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

function customerPathFor(target: CustomerRouteTarget, resourceId?: string): { path: string; requiresResourceId: boolean } {
  switch (target) {
    case 'dashboard': return { path: '', requiresResourceId: false };
    case 'companies': return { path: '/companies', requiresResourceId: false };
    case 'projects': return { path: '/projects', requiresResourceId: false };
    case 'project': return { path: `/projects/${resourceId ? encodePathSegment(resourceId) : ''}`, requiresResourceId: true };
    case 'milestones': return { path: '/milestones', requiresResourceId: false };
    case 'milestoneGate': return { path: `/milestones/${resourceId ? encodePathSegment(resourceId) : ''}/governance`, requiresResourceId: true };
    case 'calendar': return { path: '/calendar', requiresResourceId: false };
    case 'tasks': return { path: '/tasks', requiresResourceId: false };
    case 'task': return { path: `/tasks/${resourceId ? encodePathSegment(resourceId) : ''}`, requiresResourceId: true };
    case 'assets': return { path: '/assets', requiresResourceId: false };
    case 'documents': return { path: '/documents', requiresResourceId: false };
    case 'investments': return { path: '/investments', requiresResourceId: false };
    case 'decisions': return { path: '/decisions', requiresResourceId: false };
    case 'reports': return { path: '/reports', requiresResourceId: false };
    case 'chat': return { path: '/chat', requiresResourceId: false };
    case 'users': return { path: '/users', requiresResourceId: false };
    case 'invitations': return { path: '/invitations', requiresResourceId: false };
    case 'invitationSettings': return { path: '/settings/invitations', requiresResourceId: false };
    case 'security': return { path: '/security', requiresResourceId: false };
    case 'settings': return { path: '/settings', requiresResourceId: false };
    case 'billing': return { path: '/settings/billing', requiresResourceId: false };
  }
}

export function buildCustomerRoute(
  workspaceId: string | null | undefined,
  target: CustomerRouteTarget,
  options?: {
    readonly resourceId?: string;
    readonly resourceWorkspaceId?: string;
    readonly search?: RouteQueryInput | URLSearchParams | string;
  },
): RouteBuildResult {
  if (!workspaceId) {
    return { ok: false, plane: 'customer', reason: 'missing-workspace' };
  }
  if (!isValidWorkspaceIdFormat(workspaceId)) {
    return { ok: false, plane: 'customer', reason: 'invalid-workspace' };
  }
  if (options?.resourceWorkspaceId && options.resourceWorkspaceId !== workspaceId) {
    return { ok: false, plane: 'customer', reason: 'cross-workspace-resource' };
  }

  const targetPath = customerPathFor(target, options?.resourceId);
  if (targetPath.requiresResourceId && !options?.resourceId) {
    return { ok: false, plane: 'customer', reason: 'missing-resource-id' };
  }

  const href = `/workspaces/${encodePathSegment(workspaceId)}${targetPath.path}${toSearchString(options?.search)}`;
  return { ok: true, plane: 'customer', href };
}

export function customerRouteHref(
  workspaceId: string | null | undefined,
  target: CustomerRouteTarget,
  options?: {
    readonly resourceId?: string;
    readonly resourceWorkspaceId?: string;
    readonly search?: RouteQueryInput | URLSearchParams | string;
  },
): string {
  const result = buildCustomerRoute(workspaceId, target, options);
  return result.ok ? result.href : '/workspaces/select';
}

export function buildPlatformRoute(
  path: string,
  options?: { readonly search?: RouteQueryInput | URLSearchParams | string },
): RouteBuildResult {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!normalizedPath.startsWith('/platform')) {
    return { ok: false, plane: 'platform', reason: 'invalid-platform-route' };
  }
  return {
    ok: true,
    plane: 'platform',
    href: `${normalizedPath}${toSearchString(options?.search)}`,
  };
}

export function platformRouteHref(
  path: string,
  options?: { readonly search?: RouteQueryInput | URLSearchParams | string },
): string {
  const result = buildPlatformRoute(path, options);
  return result.ok ? result.href : '/platform';
}
